import { useEffect, useMemo } from "react";
import { SITE } from "@/mocks/site";

interface SeoProps {
  title: string;
  description: string;
  type?: string;
  path?: string;
  image?: string;
  jsonLd?: object | object[];
}

const descriptionTag = "meta[name='description']";
const keywordsTag = "meta[name='keywords']";

function setMeta(selector: string, attr: "content", value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    if (selector === descriptionTag) el.setAttribute("name", "description");
    if (selector === keywordsTag) el.setAttribute("name", "keywords");
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

export default function Seo({ title, description, type = "website", path = "/", image, jsonLd }: SeoProps) {
  const jsonKey = useMemo(() => {
    if (!jsonLd) return "";
    const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    return JSON.stringify(arr);
  }, [jsonLd]);

  useEffect(() => {
    document.title = title;
    setMeta(descriptionTag, "content", description);

    const url = `${SITE.url}${path}`;
    document.querySelectorAll("link[rel='canonical']").forEach((n) => n.remove());
    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = url;
    document.head.appendChild(canonical);

    document.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]').forEach((n) => n.remove());
    const tag = (attr: "property" | "name", key: string, content: string) => {
      const m = document.createElement("meta");
      m.setAttribute(attr, key);
      m.setAttribute("content", content);
      document.head.appendChild(m);
    };
    tag("property", "og:type", type);
    tag("property", "og:title", title);
    tag("property", "og:description", description);
    tag("property", "og:url", url);
    tag("property", "og:site_name", SITE.fullName);
    tag("property", "og:locale", "zh_CN");
    if (image) tag("property", "og:image", image);
    tag("name", "twitter:card", "summary_large_image");
    tag("name", "twitter:title", title);
    tag("name", "twitter:description", description);

    const node = document.createElement("script");
    node.id = "seo-jsonld";
    node.setAttribute("type", "application/ld+json");
    node.textContent = jsonKey;
    document.querySelectorAll("#seo-jsonld").forEach((n) => n.remove());
    document.head.appendChild(node);
  }, [title, description, type, path, image, jsonKey]);

  return null;
}